/**
 * Update Consumer Data Actor
 *
 * For returning patients, fires two parallel API calls after intake completes:
 * 1. REST: update email via updateContactInfoByConsumer (only if email provided)
 * 2. GraphQL: update demographics + address via updateConsumerDemographics
 *    (mirrors createUserAndConsumer — same fields, same address extraction)
 *
 * Fire-and-forget: always resolves, the machine always proceeds via onDone.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import {
  updateConsumerDemographics,
  updateContactInfoByConsumer,
} from "../../server-actions/user-actions";
import type { ActorMetadata, ActorWithMetadata } from "./send-sms.actor";

const UpdateConsumerDataInputSchema = z.object({
  consumerId: z.string().min(1),
  email: z.string(),
  firstname: z.string().min(1),
  lastname: z.string().min(1),
  dob: z.string().min(1),
  sex: z.string().min(1),
  preferredLanguage: z.string().optional(),
  ehrLocationId: z.string().optional(),
  // biome-ignore lint/suspicious/noExplicitAny: intake form sections are complex
  intakeFormSections: z.array(z.any()).optional(),
  intakeResponses: z.record(z.unknown()).optional(),
  originalData: z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      dateOfBirth: z.string().optional(),
      sex: z.string().optional(),
    })
    .optional(),
});

type UpdateConsumerDataInput = z.infer<typeof UpdateConsumerDataInputSchema>;

const updateConsumerDataActorImpl = fromPromise(
  async ({ input }: { input: UpdateConsumerDataInput }): Promise<void> => {
    const validated = UpdateConsumerDataInputSchema.safeParse(input);
    if (!validated.success) {
      datadogLogs.logger.warn("[updateConsumerData] validation failed", {
        error: validated.error.message,
      });
      return;
    }

    const {
      consumerId,
      email,
      firstname,
      lastname,
      dob,
      sex,
      preferredLanguage,
      ehrLocationId,
      intakeFormSections,
      intakeResponses,
      originalData,
    } = validated.data;

    const calls: Promise<unknown>[] = [];

    // REST: update email (only if patient provided one)
    if (email) {
      calls.push(
        updateContactInfoByConsumer({ consumerId, email }).then((result) => {
          if (!result.success) {
            datadogLogs.logger.warn(
              "[updateConsumerData] email update failed",
              {
                consumerId,
                error: result.error,
              },
            );
          }
        }),
      );
    }

    // GraphQL: update demographics + address (mirrors createUserAndConsumer)
    calls.push(
      updateConsumerDemographics({
        consumerId,
        firstname,
        lastname,
        dob,
        sex,
        preferredLanguage,
        ehrLocationId,
        intakeFormSections,
        intakeResponses,
        originalData,
      }).then((result) => {
        if (!result.success) {
          datadogLogs.logger.warn(
            "[updateConsumerData] demographics update failed",
            { consumerId, error: result.error },
          );
        }
      }),
    );

    await Promise.allSettled(calls);
  },
);

export const updateConsumerDataActor: ActorWithMetadata = {
  impl: updateConsumerDataActorImpl,
  metadata: {
    displayName: "updateConsumerData",
    description:
      "Update consumer email and demographics for a returning patient",
    category: "data",
    retryable: false,
    timeout: 15000,
    errorCodes: [],
    inputSchema: UpdateConsumerDataInputSchema,
    successSchema: z.void(),
    errorSchema: z.void(),
    onSuccess: () => ({}),
    onError: () => ({}),
  } satisfies ActorMetadata,
};
